"use client";

import { Film, AudioWaveform, Armchair, Wine } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurWellnessApproach() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const features = [{
    number: "01",
    title: "VET-APPROVED NUTRITION",
    description: "Evidence-based feeding guidelines tailored to your pet's unique metabolic needs. Simple, clear, and scientifically sound.",
    Icon: Film
  }, {
    number: "02",
    title: "ACTIVE LIFESTYLE",
    description: "Engaging exercise routines designed to keep your dog or cat moving, burning calories, and staying mentally sharp.",
    Icon: AudioWaveform
  }, {
    number: "03",
    title: "WEIGHT TRACKING",
    description: "Monitor progress with our intuitive digital tools. Visualize health milestones and celebrate every small victory.",
    Icon: Armchair
  }, {
    number: "04",
    title: "HEALTHY TREAT SWAPS",
    description: "Delicious, low-calorie alternatives that satisfy cravings without compromising your pet's wellness goals.",
    Icon: Wine
  }];
  return <section data-section-id="4118" id="our-wellness-approach" className="bg-background text-foreground">
      <div className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {}
          <div ref={headerRef} className={`flex flex-col items-center text-center transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="font-mono text-xs md:text-sm tracking-widest text-primary relative inline-block pb-2">
              <span>§ 05 / OUR WELLNESS APPROACH</span>
              <span className="absolute left-0 right-0 bottom-0 h-[2px] bg-primary" />
            </div>

            {}
            <h2 className="font-default font-bold uppercase mt-12 md:mt-16 leading-[0.95] tracking-tighter text-foreground" style={{
            fontSize: "clamp(2.5rem, 9vw, 9rem)"
          }}>
              <span className="block">NO COMPROMISE</span>
              <span className="block">ON YOUR PET'S</span>
              <span className="block text-primary">WELLNESS.</span>
            </h2>
          </div>

          {}
          <div className="mt-20 md:mt-28 lg:mt-32 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-16 lg:gap-20">
            {features.map((feature, i) => {
            const Icon = feature.Icon;
            return <FeatureItem key={i} data-index={i} number={feature.number} title={feature.title} description={feature.description} Icon={Icon} index={i} />;
          })}
          </div>

          {}
          <div ref={footerRef} className={`mt-24 md:mt-32 lg:mt-40 text-center transition-all duration-700 ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-serif italic text-foreground leading-tight" style={{
            fontSize: "clamp(1.25rem, 2.5vw, 2rem)"
          }}>
              These are not just tips. They are the foundation of a longer, happier life
              <span className="text-primary">.</span>
            </p>
          </div>
        </div>
      </div>
    </section>;
}
function FeatureItem({
  number,
  title,
  description,
  Icon,
  index,
  ...rest
}: {
  number: string;
  title: string;
  description: string;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  index: number;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} {...rest} className={`group flex flex-col transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 100}ms`
  }}>
      {}
      <div className="font-mono font-light text-primary leading-none transition-transform duration-300 group-hover:scale-110 origin-left" style={{
      fontSize: "clamp(2.5rem, 4vw, 4rem)"
    }}>
        {number}
      </div>

      {}
      <div className="relative mt-6 mb-6 h-[1.5px] w-full bg-border overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-0 bg-primary transition-all duration-500 group-hover:w-full" />
      </div>

      {}
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-default font-bold text-foreground text-xl md:text-2xl uppercase leading-tight tracking-tight">
          {title}
        </h3>
        <Icon className="size-7 md:size-8 text-primary shrink-0 transition-colors duration-300 group-hover:text-primary" strokeWidth={1.5} />
      </div>

      {}
      <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground mt-5">
        {description}
      </p>
    </div>;
}